import { useEffect } from "react";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { useTranslation } from "./LocaleProvider";

/**
 * One-shot bridge component — runs inside the auth provider context
 * and pushes ``user.preferred_language`` into the LocaleProvider
 * once the user record loads. Without this, a fresh device sign-in
 * would render in English (or the browser's default) until the user
 * manually picked their language again from Settings.
 *
 * Renders nothing. Trades a single ``useEffect`` for the cross-device
 * stickiness — localStorage handles per-device sticky already; this
 * fills the gap on first sign-in to a clean device.
 */
export const LangSyncFromUser: React.FC = () => {
  const { user } = useAuth();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (!user?.preferred_language) return;
    if (user.preferred_language === i18n.language) return;
    // Don't override if the user has already actively chosen a
    // language on this device — localStorage win means LocaleProvider
    // already has the right value. Only adopt the server preference
    // when localStorage is empty (i.e. fresh device).
    let hasLocal = false;
    try {
      hasLocal = !!window.localStorage.getItem("afri:lang");
    } catch {
      /* no-op */
    }
    if (hasLocal) return;
    i18n.changeLanguage(user.preferred_language);
  }, [user?.preferred_language, i18n]);

  return null;
};
